export type DiffType = 'unchanged' | 'added' | 'deleted' | 'modified';
export type ArrayDiff<T> = { type: DiffType; prev?: T; new?: T };

// Improved version of https://www.geeksforgeeks.org/javascript-program-for-longest-common-subsequence/
// Prefers earlier alignment: if moving left/up keeps the same LCS length, do that instead of taking the diagonal

export function findDiffs<T>(arr1: T[], arr2: T[], compareFn: (a: T, b: T) => boolean): ArrayDiff<T>[] {
  const n = arr1.length;
  const m = arr2.length;
  const dp = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(0));
  const dirs = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(''));

  // construct dp table
  for (let x = 1; x <= n; x++) {
    for (let y = 1; y <= m; y++) {
      if (compareFn(arr1[x - 1], arr2[y - 1])) {
        dp[x][y] = dp[x - 1][y - 1] + 1;
        dirs[x][y] = 'diagonal';
      } else {
        dp[x][y] = Math.max(dp[x - 1][y], dp[x][y - 1]);
        dirs[x][y] = dp[x - 1][y] > dp[x][y - 1] ? 'up' : 'left';
      }
    }
  }

  // backtrack to find the diffs
  const diffs: ArrayDiff<T>[] = [];
  let x = n;
  let y = m;

  while (x > 0 || y > 0) {
    if (x > 0 && y > 0 && dirs[x][y] === 'diagonal') {
      // Prefer earlier alignment: if moving left/up keeps the same LCS length, do that instead of taking the diagonal
      if (dp[x][y - 1] === dp[x][y]) {
        // addition
        diffs.push({ type: 'added', new: arr2[y - 1] });
        y--;
      } else if (dp[x - 1][y] === dp[x][y]) {
        // deletion
        diffs.push({ type: 'deleted', prev: arr1[x - 1] });
        x--;
      } else {
        // match found
        diffs.push({ type: 'unchanged', prev: arr1[x - 1], new: arr2[y - 1] });
        x--;
        y--;
      }
    } else if (x > 0 && (y === 0 || dirs[x][y] === 'up')) {
      // deletion
      diffs.push({ type: 'deleted', prev: arr1[x - 1] });
      x--;
    } else if (y > 0 && (x === 0 || dirs[x][y] === 'left')) {
      // addition
      diffs.push({ type: 'added', new: arr2[y - 1] });
      y--;
    }
  }

  return alignDiffs(diffs.reverse());
}

// finds the LCS using dynamic programming and backtrack to find the diffs
// https://www.geeksforgeeks.org/javascript-program-for-longest-common-subsequence/

export function findDiffsOriginal<T>(arr1: T[], arr2: T[], compareFn: (a: T, b: T) => boolean): ArrayDiff<T>[] {
  const n = arr1.length;
  const m = arr2.length;
  const dp = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(0));
  const dirs = Array(n + 1)
    .fill(null)
    .map(() => Array(m + 1).fill(''));

  // construct dp table
  for (let x = 1; x <= n; x++) {
    for (let y = 1; y <= m; y++) {
      if (compareFn(arr1[x - 1], arr2[y - 1])) {
        dp[x][y] = dp[x - 1][y - 1] + 1;
        dirs[x][y] = 'diagonal';
      } else {
        dp[x][y] = Math.max(dp[x - 1][y], dp[x][y - 1]);
        dirs[x][y] = dp[x - 1][y] > dp[x][y - 1] ? 'up' : 'left';
      }
    }
  }

  // backtrack to find the diffs
  const diffs: ArrayDiff<T>[] = [];
  let x = n;
  let y = m;

  while (x > 0 || y > 0) {
    if (x > 0 && y > 0 && dirs[x][y] === 'diagonal') {
      // match found
      diffs.push({ type: 'unchanged', prev: arr1[x - 1], new: arr2[y - 1] });
      x--;
      y--;
    } else if (x > 0 && (y === 0 || dirs[x][y] === 'up')) {
      // deletion
      diffs.push({ type: 'deleted', prev: arr1[x - 1] });
      x--;
    } else if (y > 0 && (x === 0 || dirs[x][y] === 'left')) {
      // addition
      diffs.push({ type: 'added', new: arr2[y - 1] });
      y--;
    }
  }

  return alignDiffs(diffs.reverse());
}

// Aligns the diffs into two arrays, one for the old array and one for the new array
// Places deletions and additions next to each other when possible
function alignDiffs<T>(diffs: ArrayDiff<T>[]) {
  const alignedDiffs: ArrayDiff<T>[] = [];
  let oldIdx = 0;
  let newIdx = 0;

  diffs.forEach((diff) => {
    if (diff.type === 'unchanged') {
      oldIdx = newIdx = Math.max(oldIdx, newIdx);
      alignedDiffs.push(diff);
      oldIdx++;
      newIdx++;
    } else if (diff.type === 'added') {
      if (newIdx < alignedDiffs.length) {
        alignedDiffs[newIdx].new = diff.new;
        alignedDiffs[newIdx].type = 'modified';
      } else {
        alignedDiffs.push(diff);
      }
      newIdx++;
    } else if (diff.type === 'deleted') {
      if (oldIdx < alignedDiffs.length) {
        alignedDiffs[oldIdx].prev = diff.prev;
        alignedDiffs[oldIdx].type = 'modified';
      } else {
        alignedDiffs.push(diff);
      }
      oldIdx++;
    }
  });

  return alignedDiffs;
}
